import { useCallback } from 'react';

import Cookies from 'js-cookie';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates, {
	FeatureGateEnvironment,
	FromValuesClientOptions,
	LocalOverrides,
} from '@atlaskit/feature-gate-js-client';
import { CustomAttributes, Identifiers } from '@atlassian/feature-gate-node-client-bootstrapper';

import { ATL_COHORT, Cohort } from '@/analytics/atl-analytics/cohort';
import { experiments } from '@/utils/experiment/manifest';
import { Experiment, Manifest } from '@/utils/experiment/use-experiment';
import { FeatureFlagManifest, FeatureFlag } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { delocalizePathname } from '@/utils/localization';

import { Cookie } from '../utils/cookie';

import {
	ANALYTICS_ANONYMOUS_ID_COOKIE_KEY,
	ATLASSIAN_ACCOUNT_ID_COOKIE_KEY,
	getCustomAttributes,
} from './bootstrapper';
import { FeatureGatesBootstrapValues, OverrideValues } from './types';
export const TARGET_APP = 'bxp-web-platform_web';

const getEnvironment = (): FeatureGateEnvironment => {
	switch (process.env['CLIENT_LIBRARY_ENV']) {
		case 'production':
			return FeatureGateEnvironment.Production;
		case 'staging':
			return FeatureGateEnvironment.Staging;
		case 'development':
		default:
			return FeatureGateEnvironment.Staging;
	}
};
export const findExperimentByName = (
	experiments: Manifest<unknown>,
	targetName: string,
): Experiment<unknown> | null => {
	for (const key in experiments) {
		if (experiments[key] && experiments[key].experimentName === targetName) {
			return experiments[key];
		}
	}
	return null;
};

export const findFeatureGateByName = (
	featureFlags: FeatureFlagManifest,
	targetName: string,
): FeatureFlag | null => {
	for (const key in featureFlags) {
		if (featureFlags[key] && featureFlags[key].featureFlagName === targetName) {
			return featureFlags[key];
		}
	}
	return null;
};

/* eslint-disable complexity */
export const initializeFeatureGatesClient = async (
	bootstrapValues: FeatureGatesBootstrapValues,
	options: Omit<
		FromValuesClientOptions & {
			overrideValues?: OverrideValues;
		},
		'environment' | 'targetApp' | 'sdkKey'
	> = {
		overrideValues: {
			experimentOverrides: {},
			featureGateOverrides: {},
		},
	},
) => {
	const isStatic = typeof window !== 'undefined' && window.IS_STATIC;
	const isJsmProductTour =
		typeof window !== 'undefined' &&
		delocalizePathname(window.location.pathname) === '/software/jira/service-management';
	// Playground exposures include enough bootstrap metadata to exceed the beacon budget.
	// Flush smaller batches while the page is open to reduce exposure loss on exit.
	// This mitigates the issue; the SDK still needs byte-aware batching and retries.
	const clientOptions = isJsmProductTour
		? { ...options, loggingBufferMaxSize: Math.min(options.loggingBufferMaxSize ?? 5, 5) }
		: options;
	if (!isStatic) {
		await FeatureGates.initializeFromValues(
			{
				...clientOptions,
				environment: getEnvironment(),
				targetApp: TARGET_APP,
				sdkKey: bootstrapValues.clientSdkKey,
				eventLoggingApi: 'https://xp.atlassian.com/v1/',
			},
			bootstrapValues.identifiers,
			bootstrapValues.customAttributes as CustomAttributes,
			bootstrapValues.experimentValues,
		);

		const overrideValues = options['overrideValues'];

		if (
			overrideValues &&
			((overrideValues.experimentOverrides &&
				Object.keys(overrideValues.experimentOverrides).length > 0) ||
				(overrideValues.featureGateOverrides &&
					Object.keys(overrideValues.featureGateOverrides).length > 0))
		) {
			const overrideConfig = {
				configs: {},
				gates: {},
			} as LocalOverrides;

			for (const experimentName of Object.keys(overrideValues.experimentOverrides)) {
				const experiment = findExperimentByName(experiments, experimentName);
				if (!experiment) {
					console.warn(`Experiment ${experimentName} not found`);
					continue;
				}
				const { defaultValue, parameterName } = experiment;
				const overrideValue = overrideValues.experimentOverrides[experimentName][parameterName];

				// Validate that experimentOverrideValue is a valid parameter
				if (typeof overrideValue === typeof defaultValue) {
					overrideConfig.configs[experimentName] =
						overrideValues.experimentOverrides[experimentName];
				} else {
					console.warn(
						`Expected type ${typeof defaultValue} for experiment ${experimentName}, but received ${typeof overrideValue} ${overrideValue}. Attempted override will be skipped`,
					);
				}
			}

			const featureGateOverrides = overrideValues['featureGateOverrides'];
			for (const featureFlagName of Object.keys(featureGateOverrides)) {
				const featureFlag = findFeatureGateByName(featureFlags, featureFlagName);
				if (!featureFlag) {
					console.warn(`Feature flag ${featureFlagName} not found`);
					continue;
				}
				const overrideValue = featureGateOverrides[featureFlagName];
				if (typeof overrideValue === 'boolean') {
					overrideConfig.gates[featureFlagName] = overrideValue;
				} else {
					console.warn(
						`Expected boolean for feature flag ${featureFlagName}, but received ${typeof overrideValue} ${overrideValue}. Attempted override will be skipped`,
					);
				}
			}
			FeatureGates.setOverrides(overrideConfig);
		} else {
			FeatureGates.setOverrides({ configs: {}, gates: {} });
		}
	} else {
		/**
		 * Client side initialization of FeatureGates.
		 * On the browser, get values from cookies as request headers are not available.
		 * If values don't exist in the cookie, create and set them in the cookie.
		 *  This is mainly for new users landing for the first time.
		 **/
		if (!Cookies.get(ANALYTICS_ANONYMOUS_ID_COOKIE_KEY)) {
			Cookie.writeCookie(
				ANALYTICS_ANONYMOUS_ID_COOKIE_KEY,
				`%22${bootstrapValues.identifiers.analyticsAnonymousId}%22`,
				3650,
				true,
			);
		}
		if (!Cookie.readCookie(ATL_COHORT)) {
			Cohort.writeAtlCohort({
				bucketAll: Cohort.createBucket('bucketAll'),
			});
		}

		const identifiers: Identifiers = {
			analyticsAnonymousId: decodeURIComponent(
				Cookies.get(ANALYTICS_ANONYMOUS_ID_COOKIE_KEY) || '',
			).replace(/['"]+/g, ''),
			atlassianAccountId: Cookies.get(ATLASSIAN_ACCOUNT_ID_COOKIE_KEY),
		};
		const customAttributes = getCustomAttributes({
			headers: {
				'user-agent': window.navigator.userAgent,
				cookieOverride: JSON.stringify({
					[ATL_COHORT]: Cookie.readCookie(ATL_COHORT),
					'cloud.session.token': Cookies.get('cloud.session.token'),
					'cloud.session.token.stg': Cookies.get('cloud.session.token.stg'),
				}),
			},
		});
		await FeatureGates.initialize(
			{
				...clientOptions,
				environment: getEnvironment(),
				targetApp: TARGET_APP,
				apiKey: process.env['FEATURE_GATES_KEY'] as string,
				eventLoggingApi: 'https://xp.atlassian.com/v1/',
			},
			identifiers,
			customAttributes,
		);
	}
};

export const updateFeatureGatesClientWithUserIdentifiers = async (
	bootstrapValues: FeatureGatesBootstrapValues,
	updatedIdentifiers: Identifiers,
) => {
	return FeatureGates.updateUser(
		// @ts-expect-error - FetcherOptions requires `perimeter`, which is not passed here
		{
			environment: getEnvironment(),
			targetApp: TARGET_APP,
			apiKey: process.env['FEATURE_GATES_KEY'] as string,
		},
		{ ...bootstrapValues.identifiers, ...updatedIdentifiers },
		bootstrapValues.customAttributes as CustomAttributes,
	);
};

export const useUpdateFeatureGatesClientWithTenantId = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		async ({ cloudId }: { cloudId?: string }) => {
			if (typeof window !== 'undefined' && window.featureGateBootstrapValues && cloudId) {
				try {
					await updateFeatureGatesClientWithUserIdentifiers(window.featureGateBootstrapValues, {
						tenantId: cloudId,
					});
				} catch (error) {
					createAnalyticsEvent({
						action: 'failed',
						actionSubject: 'TenantAwareStatsigClientUpdaterProvider.updateStatsigClient',
						eventType: 'operational',
						attributes: {
							error: error instanceof Error ? error.message : '',
						},
					}).fire();
				}
			}
		},
		[createAnalyticsEvent],
	);
};
